import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

// ── Property Helpers for Products ──
const getProductKey = (item) => item?.qbId || item?.ListID || item?.raw?.ListID || item?._id;

const getProductName = (item) =>
  item?.name ||
  item?.Name ||
  item?.fullyQualifiedName ||
  item?.FullName ||
  item?.raw?.Name ||
  item?.raw?.FullName ||
  "—";

const getProductFullName = (item) =>
  item?.fullyQualifiedName ||
  item?.FullName ||
  item?.raw?.FullName ||
  item?.name ||
  item?.Name ||
  item?.raw?.Name ||
  "";

const getProductDesc = (item) =>
  item?.description ??
  item?.Description ??
  item?.raw?.SalesOrPurchase?.Desc ??
  item?.raw?.ItemDesc ??
  "";

const getProductType = (item) => item?.type || item?.Type || item?.raw?.Type || "";

const getProductUnitPrice = (item) => {
  if (item?.unitPrice != null) return Number(item.unitPrice);
  if (item?.SalesPrice != null) return Number(item.SalesPrice);
  if (item?.raw?.SalesOrPurchase?.Price != null) return Number(item.raw.SalesOrPurchase.Price);
  return 0;
};

const getProductPurchaseCost = (item) => {
  if (item?.purchaseCost != null) return Number(item.purchaseCost);
  if (item?.PurchaseCost != null) return Number(item.PurchaseCost);
  if (item?.raw?.SalesOrPurchase?.PurchaseCost != null) return Number(item.raw.SalesOrPurchase.PurchaseCost);
  return 0;
};

const getProductActive = (item) => {
  if (item?.active != null) return Boolean(item.active);
  if (item?.IsActive != null) return Boolean(item.IsActive);
  if (item?.raw?.IsActive != null) return item.raw.IsActive === "true" || item.raw.IsActive === true;
  return true;
};

const getProductDate = (item) =>
  item?.lastUpdatedTime ||
  item?.updatedAt ||
  item?.TimeModified ||
  item?.raw?.TimeModified ||
  item?.createTime ||
  item?.createdAt ||
  item?.TimeCreated ||
  item?.raw?.TimeCreated;

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TypeBadge = ({ type }) => {
  const map = {
    Service: { bg: "bg-[#115B9D]/10", text: "text-[#115B9D]", border: "border-[#115B9D]/20" },
    Inventory: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
    NonInventory: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
    SalesTax: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  };
  const c = map[type] || { bg: "bg-gray-100", text: "text-gray-600", border: "border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
      {type || "—"}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${active ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6] border" : "bg-gray-100 text-gray-500 border border-gray-200"}`}>
    <span className={`size-1.5 rounded-full ${active ? "bg-[#0a7822a6]" : "bg-gray-400"}`} />
    {active ? "Active" : "Inactive"}
  </span>
);

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function QBDProductList() {
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchItems = async () => {
    loader(true);
    await ApiClient.get(`items?companyId=${companyId}`)
      .then((res) => { if (res?.success) setItems(res?.data || []); })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const uniqueTypes = [...new Set(items.map((i) => getProductType(i)).filter(Boolean))];

  const filteredItems = items.filter((item) => {
    const q = searchQuery.toLowerCase();
    const name = getProductName(item);
    const fullName = getProductFullName(item);
    const desc = getProductDesc(item);
    const type = getProductType(item);
    const active = getProductActive(item);

    const matchesSearch =
      !q ||
      name.toLowerCase().includes(q) ||
      fullName.toLowerCase().includes(q) ||
      desc.toLowerCase().includes(q) ||
      type.toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === "" ? true : statusFilter === "active" ? active === true : active === false;
    const matchesType = typeFilter === "" ? true : type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const activeCount = items.filter((i) => getProductActive(i)).length;

  const closeModal = () => { setIsViewModalOpen(false); setSelectedItem(null); };

  const typeColorMap = (type) => {
    const map = {
      Service: { bg: "bg-[#115B9D]/10", text: "text-[#115B9D]", border: "border-[#115B9D]/20" },
      Inventory: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
      NonInventory: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
      SalesTax: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    };
    return map[type] || { bg: "bg-gray-100", text: "text-gray-600", border: "border-gray-200" };
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">QB Desktop – Products & Services</h2>
            <p className="text-sm text-gray-600">{items.length} item{items.length !== 1 ? "s" : ""} synced</p>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0FEF4] border border-[#008236]/20 rounded-xl">
            <span className="size-2 rounded-full bg-[#008236] animate-pulse" />
            <span className="text-xs font-medium text-[#008236]">QB Desktop Connected</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: "Total Items", value: items.length, bg: "bg-[#115B9D17]", icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#115B9D]"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg> },
            { label: "Active Items", value: activeCount, bg: "bg-[#f0fdf4]", icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#008236]"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg> },
            { label: "Inactive Items", value: items.length - activeCount, bg: "bg-amber-50", icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" /></svg> },
          ].map((card) => (
            <div key={card.label} className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4">
              <div className={`size-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}>{card.icon}</div>
              <div>
                <p className="text-xs text-gray-600 font-medium">{card.label}</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* Filters */}
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
              <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
                {STATUS_TABS.map((tab) => {
                  const count =
                    tab.value === ""
                      ? items.length
                      : tab.value === "active"
                        ? activeCount
                        : items.length - activeCount;
                  const isActive = statusFilter === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setStatusFilter(tab.value)}
                      className={`whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${isActive
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      {tab.label}
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] ${isActive
                          ? "bg-[#115B9D]/10 text-[#115B9D] font-bold"
                          : "bg-gray-200/70 text-gray-600"
                          }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="relative w-full sm:w-[350px]">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
                <input type="text" placeholder="Search by name, type..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all" />
              </div>
            </div>
            {uniqueTypes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-600 font-medium">Type:</span>
                <button type="button" onClick={() => setTypeFilter("")}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${typeFilter === "" ? "bg-[#115B9D] text-white border-[#115B9D]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
                  All
                </button>
                {uniqueTypes.map((type) => {
                  const c = typeColorMap(type);
                  const isActive = typeFilter === type;
                  return (
                    <button key={type} type="button" onClick={() => setTypeFilter(isActive ? "" : type)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${isActive ? `${c.bg} ${c.text} ${c.border}` : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
                      {type}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full min-w-[900px] table-auto">
              <colgroup>
                <col style={{ width: "6%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Actions", "Item", "Description", "Type", "Unit Price", "Purchase Cost", "Status", "Last Updated"].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium block">{h}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                            <path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                            <path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">No items found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item, index) => {
                    const name = getProductName(item);
                    const fullName = getProductFullName(item);
                    const desc = getProductDesc(item);
                    const type = getProductType(item);
                    const unitPrice = getProductUnitPrice(item);
                    const purchaseCost = getProductPurchaseCost(item);
                    const active = getProductActive(item);
                    const updatedDate = getProductDate(item);
                    const itemKey = getProductKey(item) || index;

                    return (
                      <tr key={itemKey} className="hover:bg-gray-50/60 transition-colors align-middle">
                        <td className="py-2.5 px-3 align-middle">
                          <button type="button" onClick={() => { setSelectedItem(item); setIsViewModalOpen(true); }} title="View Details"
                            className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="size-8 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-semibold text-[#115B9D]">{getInitials(name)}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 break-words leading-tight">{name}</p>
                              {fullName && fullName !== name && (
                                <p className="text-[11px] text-gray-600 break-words leading-tight">{fullName}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <p className="text-xs text-gray-500 break-words leading-snug">{desc || "—"}</p>
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <TypeBadge type={type} />
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs font-medium text-gray-800 whitespace-nowrap">{formatCurrency(unitPrice)}</p>
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs text-gray-500 whitespace-nowrap">{formatCurrency(purchaseCost)}</p>
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <StatusBadge active={active} />
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs text-gray-700 break-words leading-tight">{formatDate(updatedDate)}</p>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 mt-3">
            {filteredItems.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <p className="text-sm text-gray-500">No items found</p>
              </div>
            ) : (
              filteredItems.map((item, index) => {
                const name = getProductName(item);
                const desc = getProductDesc(item);
                const type = getProductType(item);
                const unitPrice = getProductUnitPrice(item);
                const active = getProductActive(item);
                const itemKey = getProductKey(item) || index;

                return (
                  <div key={itemKey} className="border border-gray-200 rounded-xl p-4 bg-white">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-[#115B9D]">{getInitials(name)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 break-words">{name}</p>
                          {desc && <p className="text-xs text-gray-600 break-words">{desc}</p>}
                        </div>
                      </div>
                      <button type="button" onClick={() => { setSelectedItem(item); setIsViewModalOpen(true); }} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Type</span>
                      <TypeBadge type={type} />
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Price</span>
                      <p className="text-sm font-medium text-gray-800">{formatCurrency(unitPrice)}</p>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Status</span>
                      <StatusBadge active={active} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="size-12 rounded-2xl bg-[#115B9D17] flex items-center justify-center">
                        <span className="text-sm font-bold text-[#115B9D]">{getInitials(getProductName(selectedItem))}</span>
                      </div>
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">{getProductName(selectedItem)}</Dialog.Title>
                        <TypeBadge type={getProductType(selectedItem)} />
                      </div>
                    </div>
                    <button type="button" onClick={closeModal} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { label: "Description", value: getProductDesc(selectedItem) },
                      { label: "Unit Price", value: formatCurrency(getProductUnitPrice(selectedItem)) },
                      { label: "Purchase Cost", value: formatCurrency(getProductPurchaseCost(selectedItem)) },
                      { label: "Income Account", value: selectedItem?.incomeAccountRef?.fullName || selectedItem?.incomeAccountRef?.name || selectedItem?.raw?.SalesOrPurchase?.AccountRef?.FullName },
                      { label: "Expense Account", value: selectedItem?.expenseAccountRef?.fullName || selectedItem?.expenseAccountRef?.name },
                      { label: "Asset Account", value: selectedItem?.assetAccountRef?.fullName || selectedItem?.assetAccountRef?.name },
                      { label: "Status", value: getProductActive(selectedItem) ? "Active" : "Inactive" },
                    ].map(({ label, value }) =>
                      value ? (
                        <div key={label} className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
                          <p className="text-xs text-gray-500 font-medium">{label}</p>
                          <p className="text-sm text-gray-800 font-medium text-right max-w-[60%] break-words">{value}</p>
                        </div>
                      ) : null
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}

